import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPixel } from '../lib/metaPixel';
import { Play, Users, Star, ChevronRight, Check, Youtube, Mic, Video, TrendingUp, ShoppingCart, Lock } from 'lucide-react';
import SEO from '../components/SEO';
import YouTuberSignupWizard from '../components/YouTuberSignupWizard';
import SlotsTodayBadge from '../components/SlotsTodayBadge';
import BundleCheckoutModal from '../components/BundleCheckoutModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUNDLES = {
  10: { size: 10, label: '10-Visit Bundle', amount: 6792, original: 7990, discount: 15 },
  20: { size: 20, label: '20-Visit Bundle', amount: 11985, original: 15980, discount: 25 },
};

const YouTuberProgram = () => {
  const [showSignup, setShowSignup] = useState(false);
  const [stats, setStats] = useState({ total_signups: 0, free_slots_remaining: 40, target: 100 });
  const [checkoutBundle, setCheckoutBundle] = useState(null);

  useEffect(() => {
    fetchStats();
    // Handle return from Cashfree
    const params = new URLSearchParams(window.location.search);
    const status = params.get('bundle_payment');
    if (status === 'success') {
      toast.success("Payment successful! Check your email for the receipt — we'll be in touch within 24 hours.", { duration: 8000 });
      // Meta Pixel: Purchase event. Bundle size lives in `bundle` param if Cashfree return preserved it; else we omit value to avoid lying to the pixel.
      const bundleSize = parseInt(params.get('bundle_size'), 10);
      const amount = parseInt(params.get('amount'), 10);
      trackPixel('Purchase', {
        content_name: bundleSize ? `${bundleSize}-Visit Bundle` : 'YouTuber Bundle',
        content_category: 'YouTuberBundle',
        content_ids: [params.get('order_id') || 'unknown'],
        value: Number.isFinite(amount) ? amount : undefined,
        currency: 'INR',
      });
      window.history.replaceState({}, document.title, '/youtuber-program');
    } else if (status === 'pending') {
      toast.info("Payment is being processed. We'll email you the receipt as soon as it's confirmed.", { duration: 6000 });
      window.history.replaceState({}, document.title, '/youtuber-program');
    } else if (status === 'error' || status === 'failed') {
      toast.error("Payment didn't go through. Please try again or reach us on WhatsApp.", { duration: 6000 });
      window.history.replaceState({}, document.title, '/youtuber-program');
    }
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/youtuber-program/stats`);
      setStats(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const steps = [
    { icon: Mic, title: 'We Help You Find Your Niche', desc: 'Cooking, spirituality, gardening, storytelling, life lessons - your experience is your content.' },
    { icon: Video, title: 'DigiMitra Sets Up Your Channel', desc: 'Our trained DigiMitra visits your home and creates your YouTube channel, profile, and first video.' },
    { icon: TrendingUp, title: 'Weekly Guidance & Growth', desc: 'Regular calls to help you plan content, improve quality, and grow your audience week by week.' },
  ];

  return (
    <div className="yt-program-page" data-testid="youtuber-program-page">
      <SEO
        title="Elderra YouTuber Program | Help Seniors Start YouTube Channels"
        description="Join Elderra's program to help 100 inspiring people above 60 start their YouTube channels. First 40 signups free. DigiMitra home visit for setup and weekly guidance calls."
        keywords="senior YouTubers India, elderly content creators, YouTube for seniors, Elderra YouTuber program, digital empowerment elderly"
      />

      {/* Hero Section */}
      <section className="yt-hero">
        <div className="container">
          <div className="yt-hero-badge" data-testid="yt-hero-badge">
            <Play size={16} /> New Program
          </div>
          <h1 data-testid="yt-hero-title">Elderra YouTuber Program</h1>
          <p className="yt-hero-sub">
            Helping <strong>100 inspiring people above 60</strong> share their wisdom with the world through YouTube
          </p>

          <div className="yt-hero-stats" data-testid="yt-hero-stats">
            <div className="yt-stat-pill">
              <span className="yt-stat-num">{stats.free_slots_remaining}</span>
              <span className="yt-stat-label">Free slots left</span>
            </div>
            <div className="yt-stat-pill">
              <span className="yt-stat-num">{stats.total_signups}</span>
              <span className="yt-stat-label">Signed up</span>
            </div>
            <div className="yt-stat-pill">
              <span className="yt-stat-num">100</span>
              <span className="yt-stat-label">Target creators</span>
            </div>
          </div>

          <Button className="yt-cta-btn" onClick={() => setShowSignup(true)} data-testid="yt-signup-btn">
            Join the Program <ChevronRight size={20} />
          </Button>
          <SlotsTodayBadge serviceType="youtuber" accent="yt" testId="yt-slots-today" />
          <p className="yt-cta-note">First visit absolutely free of cost for the first 40 signups</p>
        </div>
      </section>

      {/* Why YouTube Section */}
      <section className="yt-why">
        <div className="container">
          <h2 data-testid="yt-why-title">Why YouTube for Seniors?</h2>
          <p className="yt-why-sub">
            At Elderra, we believe YouTube is a great platform for India's elder population to find an audience. 
            They get to share their learnings and analyse topics of their choice.
          </p>
          <div className="yt-why-grid">
            <div className="yt-why-card">
              <Youtube size={32} />
              <h3>Share Your Story</h3>
              <p>Decades of experience deserve an audience. Your recipes, life lessons, and skills can inspire millions.</p>
            </div>
            <div className="yt-why-card">
              <Users size={32} />
              <h3>Build a Community</h3>
              <p>Connect with people who value your perspective. Find your tribe across India and beyond.</p>
            </div>
            <div className="yt-why-card">
              <Star size={32} />
              <h3>Earn Revenue</h3>
              <p>For some, this can potentially become a source of income through YouTube's monetization program.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="yt-how">
        <div className="container">
          <h2 data-testid="yt-how-title">How It Works</h2>
          <div className="yt-steps">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="yt-step-card" data-testid={`yt-step-${i + 1}`}>
                  <div className="yt-step-num">{i + 1}</div>
                  <div className="yt-step-icon"><Icon size={28} /></div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="yt-pricing">
        <div className="container">
          <h2 data-testid="yt-pricing-title">Simple Pricing</h2>
          <p className="yt-pricing-sub">Start free, then save more with a bundle.</p>
          <div className="yt-pricing-cards">
            <div className="yt-price-card yt-price-free" data-testid="yt-price-free">
              <div className="yt-price-badge">Limited Offer</div>
              <h3>First 40 Signups</h3>
              <div className="yt-price-amount">FREE</div>
              <p>First visit free of cost — channel setup at your home</p>
              <ul>
                <li><Check size={16} /> Home visit for channel setup</li>
                <li><Check size={16} /> Topic & niche identification</li>
                <li><Check size={16} /> First video recording help</li>
                <li><Check size={16} /> Guided by trained DigiMitra</li>
              </ul>
              <div className="yt-price-slots">{stats.free_slots_remaining} of 40 slots remaining</div>
            </div>

            <div className="yt-price-card" data-testid="yt-price-paid">
              <h3>Single Visit</h3>
              <div className="yt-price-amount">&#8377;799 <span>per visit</span></div>
              <p>Pay-as-you-go after your free first visit</p>
              <ul>
                <li><Check size={16} /> Home visit by DigiMitra</li>
                <li><Check size={16} /> Hands-on recording support</li>
                <li><Check size={16} /> Upload & optimization help</li>
                <li><Check size={16} /> Book one at a time, no commitment</li>
              </ul>
            </div>

            <div className="yt-price-card yt-price-bundle" data-testid="yt-price-bundle-10">
              <div className="yt-price-badge yt-bundle-badge">15% Off · Early Bird</div>
              <h3>10-Visit Bundle</h3>
              <div className="yt-price-amount yt-price-bundle-amount">
                &#8377;6,792
                <span className="yt-price-strike">&#8377;7,990</span>
              </div>
              <div className="yt-price-per">≈ &#8377;679 / visit · save &#8377;1,198</div>
              <p>Perfect for a 2-3 month creator journey</p>
              <ul>
                <li><Check size={16} /> 10 weekly DigiMitra visits</li>
                <li><Check size={16} /> Full editing & upload support</li>
                <li><Check size={16} /> Topic calendar & growth plan</li>
                <li><Check size={16} /> Priority slot booking</li>
              </ul>
              <Button
                className="yt-bundle-buy-btn"
                onClick={() => setCheckoutBundle(BUNDLES[10])}
                data-testid="yt-bundle-buy-10"
              >
                <ShoppingCart size={15} /> Buy 10-Pack
              </Button>
            </div>

            <div className="yt-price-card yt-price-bundle yt-price-best" data-testid="yt-price-bundle-20">
              <div className="yt-price-badge yt-bundle-badge yt-best-badge">25% Off · Best Value</div>
              <h3>20-Visit Bundle</h3>
              <div className="yt-price-amount yt-price-bundle-amount">
                &#8377;11,985
                <span className="yt-price-strike">&#8377;15,980</span>
              </div>
              <div className="yt-price-per">≈ &#8377;599 / visit · save &#8377;3,995</div>
              <p>For serious creators ready to grow</p>
              <ul>
                <li><Check size={16} /> 20 weekly DigiMitra visits</li>
                <li><Check size={16} /> Channel monetization setup</li>
                <li><Check size={16} /> Thumbnail & SEO assistance</li>
                <li><Check size={16} /> Personal growth coach for 5 months</li>
              </ul>
              <Button
                className="yt-bundle-buy-btn yt-bundle-buy-best"
                onClick={() => setCheckoutBundle(BUNDLES[20])}
                data-testid="yt-bundle-buy-20"
              >
                <Lock size={14} /> Buy 20-Pack &mdash; Best Value
              </Button>
            </div>
          </div>
          <div className="yt-pricing-cta">
            <Button className="yt-cta-btn" onClick={() => setShowSignup(true)} data-testid="yt-pricing-signup-btn">
              Sign Up for Free First Visit <ChevronRight size={20} />
            </Button>
            <div className="yt-pricing-fineprint">Or buy a bundle above to lock in early bird pricing.</div>
            <div data-testid="yt-gst-note" style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
              All prices inclusive of 18% GST · Billed by GiantTreeTechnology Pvt Ltd · GSTIN 07AAICG7122N1ZA
            </div>
          </div>
        </div>
      </section>

      {/* Elderra Promise */}
      <section className="yt-promise">
        <div className="container">
          <h2>The Elderra Promise</h2>
          <p>
            Under Elderra's guidance, we will help you identify your topics and niche and hand-hold you 
            through the entire journey. Our trained DigiMitra service providers will visit your home, set up 
            your channel, and support you every week.
          </p>
          <Link to="/donate">
            <Button variant="outline" className="yt-donate-btn">Support This Mission</Button>
          </Link>
        </div>
      </section>

      {/* Signup Wizard */}
      <YouTuberSignupWizard open={showSignup} onOpenChange={(val) => { setShowSignup(val); if (!val) fetchStats(); }} stats={stats} />
      
      {/* Bundle Checkout Modal */}
      <BundleCheckoutModal
        open={!!checkoutBundle}
        onOpenChange={(val) => { if (!val) setCheckoutBundle(null); }}
        bundle={checkoutBundle}
      />
    </div>
  );
};

export default YouTuberProgram;
